"use client";

import { EventName } from "@mw/shared/analyticsEventNames";
import { publicUrls } from "@/constants";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { useTranslations } from "next-intl";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import IconCrossSmall from "@ui/central-icons/icons/IconCrossSmall";
import { clsx } from "clsx";

export function LimitReachedOverlay({
  title,
  description,
  eventName,
  eventProperties,
  ctaLink,
  buttonText,
  onClose,
  show = false,
}: {
  title: string;
  description: string;
  eventName?: EventName;
  eventProperties?: Record<string, unknown>;
  ctaLink?: string;
  buttonText?: string;
  onClose?: () => void;
  show?: boolean;
}) {
  const t = useTranslations("ClientKeys.Globals");
  const tTTS = useTranslations("ClientKeys.TTS");

  return (
    <AnimatePresence>
      {show && (
        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={clsx(
            "tw-absolute tw-inset-0 tw-z-40 tw-bg-white tw-rounded-3xl tw-text-center tw-overflow-y-auto",
            onClose
              ? "tw-grid tw-gap-6 tw-grid-rows-[1fr_auto_1fr]"
              : "tw-py-6 tw-flex tw-items-center tw-justify-center",
          )}
        >
          {onClose && (
            <div className='tw-justify-self-end tw-px-3 tw-pt-3'>
              <button
                type='button'
                className='tw-size-9 tw-flex tw-items-center tw-justify-center tw-rounded-full hover:tw-bg-gray-100 tw-transition-colors tw-outline-none focus-visible:tw-outline-focus'
                aria-label={t("close")}
                onClick={onClose}
              >
                <IconCrossSmall className='tw-size-6' />
              </button>
            </div>
          )}
          <div className={clsx("tw-px-6", onClose ? "tw-row-start-2" : "")}>
            <p className='tw-type-sm tw-font-medium tw-text-black'>{title}</p>
            <p className='tw-mt-4 tw-mb-6 tw-type-base tw-text-balance tw-text-black'>{description}</p>
            <LinkTracked
              asButton
              href={ctaLink ?? publicUrls.signUp}
              buttonVariants={{ size: "md" }}
              eventName={eventName ?? EventName.mw_limit_reached_overlay_sign_up_clicked}
              eventProperties={eventProperties}
            >
              {buttonText ?? tTTS("widget.quota_exceeded_button")}
            </LinkTracked>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
}
